<template>
  <div>
    <validation-observer ref="observer">
      <labeled-wrapper :label="$t('general') | capitalize">
        <b-row>
          <b-col cols="6">
            <distribution-detail-field :label="$t('distribution_methodology') | capitalize"
                                       :value="methodologyDisplay(distribution.distributionMethodology)"/>
          </b-col>
          <b-col>
            <distribution-detail-field
                :label="$t('description') | capitalize"
                :value="distribution.description"
                :edit-mode="editMode && !hasTransactions && !transactionsRunning"
                vid="description"
                ref="description"
                rules="required"
            >
              <template v-slot:input="{ state }">
                <b-form-input required v-model="distribution.description" :state="state"/>
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <distribution-detail-field
                :label="$t('tariff') | capitalize"
                :value="tariffDisplay(distribution.tariff)"
                :edit-mode="editMode && !hasTransactions && !transactionsRunning && !connectTariffPeriod && !internationalTariffPeriod"
                ref="tariff"
                vid="tariff"
            >
              <template v-slot:input="{ state }">
                <b-form-select required v-model="distribution.tariff" :options="tariffOptions" :state="state"/>
              </template>
            </distribution-detail-field>
          </b-col>
          <b-col>
            <distribution-detail-field :label="$t('college') | capitalize"
                                       :value="$t(distribution.college) | capitalize"/>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <distribution-detail-field
                :label="$t('period_start_date') | capitalize"
                :value="distribution.periodStart | date($config.DATE_FORMAT)"
                :edit-mode="editMode && !hasTransactions && !transactionsRunning && !internationalTariffPeriod"
                ref="periodStart"
                vid="periodStart"
                rules="required"
            >
              <template v-slot:input="{ state }">
                <date-picker
                    type="date"
                    v-model="distribution.periodStart"
                    :value-type="$config.ISO_DATE_FORMAT"
                    :class="{invalid: state === false}"
                    :format="$config.DATE_FORMAT"
                />
              </template>
            </distribution-detail-field>
          </b-col>
          <b-col>
            <distribution-detail-field
                :label="$t('period_end_date') | capitalize"
                :value="distribution.periodEnd | date($config.DATE_FORMAT)"
                :edit-mode="editMode && !hasTransactions && !transactionsRunning && !internationalTariffPeriod"
                ref="periodEnd"
                vid="periodEnd"
                :rules="`required|after:@periodStart,${$config.ISO_DATE_FORMAT},,,${$config.DATE_FORMAT}`"
            >
              <template v-slot:input="{ state }">
                <date-picker
                    type="date"
                    v-model="distribution.periodEnd"
                    :value-type="$config.ISO_DATE_FORMAT"
                    :class="{invalid: state === false}"
                    :format="$config.DATE_FORMAT"
                />
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <distribution-detail-field :label="$t('country') | capitalize"
                                       :value="countryDisplay(distribution.country)"/>
          </b-col>
        </b-row>

      </labeled-wrapper>
      <labeled-wrapper
          v-if="standardTariffPeriod"
          :label="$t('proxy') | capitalize"
          style="margin-top: 25px"
      >
        <template>
          <b-row>
            <b-col>
              <distribution-detail-field :label="$t('use_proxy_tariff') | capitalize"
                                         :value="distribution.useProxyTariff"/>
            </b-col>
            <b-col>
              <distribution-detail-field :label="$t('proxy_tariff') | capitalize" :value="distribution.proxyTariff"/>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <distribution-detail-field :label="$t('proxy_period_start_date') | capitalize"
                                         :value="distribution.proxyPeriodStart | date($config.DATE_FORMAT)"/>
            </b-col>
            <b-col>
              <distribution-detail-field :label="$t('proxy_period_end_date') | capitalize"
                                         :value="distribution.proxyPeriodEnd | date($config.DATE_FORMAT)"/>
            </b-col>
          </b-row>
        </template>
      </labeled-wrapper>
      <labeled-wrapper :label="$t('revenue') | capitalize" style="margin-top: 25px">
        <b-row>
          <template
              v-if="distribution.college === distributionCollegeTypes.PERFORMER || distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
            <b-col cols="6">
              <distribution-detail-field
                  :label="revenueLabel('performer', internationalTariffPeriod) | capitalize"
                  :edit-mode="editMode && standardTariffPeriod"
                  rules="double|positive"
                  vid="revenuePerformer"
                  ref="revenuePerformer"
              >
                <template v-slot:value v-if="distribution.revenuePerformer">
                  {{ distribution.revenueCurrency }} {{ distribution.revenuePerformer | formatNumber(2) }}
                </template>
                <template v-slot:input="{ state }">
                  <b-input-group :append="distribution.revenueCurrency">
                    <b-form-input v-model="distribution.revenuePerformer" :state="state"/>
                  </b-input-group>
                </template>
              </distribution-detail-field>
            </b-col>
            <b-col cols="6" v-if="internationalTariffPeriod">
              <distribution-detail-field
                  :label="revenueLabel('performer', false) | capitalize">
                <template v-slot:value v-if="distribution.computedConsumerFields?.internationalRevenuePerformer">
                  CAD {{ distribution.computedConsumerFields.internationalRevenuePerformer | formatNumber(2) }}
                </template>
              </distribution-detail-field>
            </b-col>
          </template>
          <template
              v-if="distribution.college === distributionCollegeTypes.MAKER || distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
            <b-col cols="6">
              <distribution-detail-field
                  :label="revenueLabel('maker', internationalTariffPeriod) | capitalize"
                  :edit-mode="editMode && standardTariffPeriod"
                  rules="double|positive"
                  vid="revenueMaker"
                  ref="revenueMaker"
              >
                <template v-slot:value v-if="distribution.revenueMaker">
                  {{ distribution.revenueCurrency }} {{ distribution.revenueMaker | formatNumber(2) }}
                </template>
                <template v-slot:input="{ state }">
                  <b-input-group :append="distribution.revenueCurrency">
                    <b-form-input v-model="distribution.revenueMaker" :state="state"/>
                  </b-input-group>
                </template>
              </distribution-detail-field>
            </b-col>
            <b-col cols="6" v-if="internationalTariffPeriod">
              <distribution-detail-field
                  :label="revenueLabel('maker', false) | capitalize">
                <template v-slot:value v-if="distribution.computedConsumerFields?.internationalRevenueMaker">
                  CAD {{ distribution.computedConsumerFields.internationalRevenueMaker | formatNumber(2) }}
                </template>
              </distribution-detail-field>
            </b-col>
          </template>
        </b-row>
        <b-row>
          <b-col cols="6"
                 v-if="distribution.college === distributionCollegeTypes.PERFORMER || distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
            <distribution-detail-field
                :label="`${$t('reserve')} (${$t('performer')})` | capitalize"
                :edit-mode="editMode && !trackSpecificTariffPeriod"
                :rules="'double|positive' + (distribution.reserveValueTypePerformer === distributionReserveValueTypes.PERCENT ? '|max_value:100' : '')"
                :detect-input="false"
                vid="revenueReservePerformer"
                ref="revenueReservePerformer"
            >
              <template v-slot:value v-if="distribution.revenueReservePerformer">
              <span v-if="distribution.reserveValueTypePerformer === distributionReserveValueTypes.PERCENT">
                {{ distribution.revenueReservePerformer }}%
              </span>
                <span v-else>
                {{ distribution.revenueCurrency }} {{ distribution.revenueReservePerformer | formatNumber(2) }}
              </span>
              </template>
              <template v-slot:input="{ state, validate }">
                <b-input-group>
                  <template v-slot:append>
                    <b-form-select :options="reserveTypeOptions" v-model="distribution.reserveValueTypePerformer"
                                   @change="validate(distribution.revenueReservePerformer)"/>
                  </template>
                  <b-form-input
                      @hook:mounted="validate(distribution.revenueReservePerformer)"
                      number
                      v-model="distribution.revenueReservePerformer"
                      :state="state"
                      @input="validate"
                  />
                </b-input-group>
              </template>
            </distribution-detail-field>
          </b-col>
          <b-col cols="6"
                 v-if="distribution.college === distributionCollegeTypes.MAKER || distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
            <distribution-detail-field
                :label="`${$t('reserve')} (${$t('maker')})` | capitalize"
                :edit-mode="editMode && !trackSpecificTariffPeriod"
                :detect-input="false"
                :rules="'double|positive' + (distribution.reserveValueTypeMaker === distributionReserveValueTypes.PERCENT ? '|max_value:100' : '')"
                vid="revenueReserveMaker"
                ref="revenueReserveMaker"
            >
              <template v-slot:value v-if="distribution.revenueReserveMaker">
              <span v-if="distribution.reserveValueTypeMaker === distributionReserveValueTypes.PERCENT">
                {{ distribution.revenueReserveMaker }}%
              </span>
                <span v-else>
                {{ distribution.revenueCurrency }} {{ distribution.revenueReserveMaker | formatNumber(2) }}
              </span>
              </template>
              <template v-slot:input="{ state, validate}">
                <b-input-group>
                  <template v-slot:append>
                    <b-form-select :options="reserveTypeOptions" v-model="distribution.reserveValueTypeMaker"
                                   @change="validate(distribution.revenueReserveMaker)"/>
                  </template>
                  <b-form-input
                      @hook:mounted="validate(distribution.revenueReserveMaker)"
                      number
                      v-model="distribution.revenueReserveMaker"
                      :state="state"
                      @input="validate"
                  />
                </b-input-group>
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
        <b-row v-if="!standardTariffPeriod">
          <b-col cols="6">
            <distribution-detail-field :label="$t('filename') | capitalize" :value="distribution.filename"/>
          </b-col>
          <b-col cols="6" v-if="!internationalTariffPeriod">
            <distribution-detail-field :label="$t('invoice_number') | capitalize" :value="distribution.invoiceNumber"/>
          </b-col>
        </b-row>
        <b-row v-if="internationalTariffPeriod">
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('exchange_rate') | capitalize"
                :value="distribution.exchangeRate"
                :edit-mode="editMode"
                rules="double|positive"
                vid="exchangeRate"
                ref="exchangeRate"
            >
              <template v-slot:input="{ state }">
                <b-form-input v-model="distribution.exchangeRate" :state="state"/>
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
      </labeled-wrapper>
    </validation-observer>
  </div>
</template>

<script>
import {
  canadaCurrencyCode,
  distributionCollegeTypes,
  distributionMethodologies,
  distributionReserveValueTypes
} from "@/constants"
import { mapActions, mapState } from "vuex"
import DistributionDetailField from "@/components/DistributionDetailField.vue"
import LabeledWrapper from "../../../components/LabeledWrapper.vue"
import { ValidationObserver } from "vee-validate"
import { capitalize } from "lodash"
import { distributionSectionMixin } from "@/utils/mixins"

export default {
  name: "MainSection",
  components: { LabeledWrapper, DistributionDetailField, ValidationObserver },
  props: {
    distribution: Object,
    editMode: Boolean,
    hasTransactions: Boolean,
    transactionsRunning: Boolean,
    connectTariffPeriod: Boolean,
    internationalTariffPeriod: Boolean,
    standardTariffPeriod: Boolean,
  },
  mixins: [distributionSectionMixin],
  data () {
    return {
      canadaCurrencyCode,
      distributionCollegeTypes,
      distributionMethodologies,
      distributionReserveValueTypes,
      reserveTypeOptions: [
        {
          text: "%",
          value: distributionReserveValueTypes.PERCENT,
        },
        {
          text: "CAD",
          value: distributionReserveValueTypes.MONEY,
        }
      ]

    }
  },
  computed: {
    ...mapState("consts", ["countries", "tariffs", "additionalTariffs"]),
    tariffOptions () {
      return this.allTariffs.filter(t => t.copyright === "ER").map(t => ({
        text: `${t.code} - ${t.text}`,
        value: t.code,
      }))
    },
    allTariffs () {
      return this.tariffs.concat(this.additionalTariffs)
    },
    trackSpecificTariffPeriod () {
      return this.distribution.distributionMethodology === distributionMethodologies.CONNECT_TRACK_SPECIFIC || this.distribution.distributionMethodology === distributionMethodologies.INTERNATIONAL
    }
  },
  methods: {
    ...mapActions("consts", ["getCountries", "getTariffs", "getAdditionalTariffs"]),
    methodologyDisplay (distributionMethodology) {
      switch (distributionMethodology) {
      case distributionMethodologies.STANDARD:
        return capitalize(this.$t("standard_usage_based"))
      case distributionMethodologies.INTERNATIONAL:
        return capitalize(this.$t("international"))
      case distributionMethodologies.CONNECT_OPEN:
        return capitalize(this.$t("connect_open"))
      case distributionMethodologies.CONNECT_ONE_TIME:
        return capitalize(this.$t("connect_one_time"))
      case distributionMethodologies.CONNECT_TRACK_SPECIFIC:
        return capitalize(this.$t("connect_track_specific"))
      }
    },
    countryDisplay (alpha3) {
      let country = this.countries.find(c => c.alpha3 === alpha3)
      return country ? country.text : alpha3
    },
    tariffDisplay (tariffCode) {
      if (tariffCode === "international") {
        return capitalize(this.$t("international"))
      }
      if (this.tariffs) {
        let tariff = this.tariffs.find(t => t.code === String(tariffCode))
        return tariff ? `${tariff.code} - ${tariff.text}` : ""
      }
    },
    revenueLabel (college, isOriginalCurrency) {
      let label = `${this.$t("revenue")}`
      if (isOriginalCurrency) {
        label += ` (${this.$t("original_currency")})`
      }
      label += ` (${this.$t(college)})`
      return label
    }
  },
  mounted () {
    this.getCountries()
    this.getTariffs().then(() => this.getAdditionalTariffs())
  }
}
</script>
